import { useEffect, useState } from "react";
import { Plus, Trash2, Send, Copy, Check, Mail, CheckCircle2, XCircle, MinusCircle, Loader2, Users, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TopNav } from "@/components/TopNav";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_LABELS = {
  admin: "Admin", general_manager: "General Manager", franchisee: "Franchisee",
  manager_marketing: "Marketing Mgr", manager_catering: "Catering Mgr", manager_event: "Event Mgr",
};
const fmtRole = (r) => ROLE_LABELS[r] || r;

const Inp = (props) => (
  <input {...props} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent" />
);
const Lbl = ({ children }) => (
  <span className="text-xs uppercase tracking-widest font-medium text-slate-500">{children}</span>
);

const StatusBadge = ({ status }) => {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    invited: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <span className={`text-xs border px-2 py-0.5 ${map[status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{status}</span>;
};

const EMAIL_STATUS = {
  sent: { icon: CheckCircle2, cls: "text-emerald-700", label: "Sent" },
  failed: { icon: XCircle, cls: "text-red-600", label: "Failed" },
  skipped: { icon: MinusCircle, cls: "text-slate-400", label: "Email off" },
};
const TYPE_LABELS = { invite: "Invite", "invite-resend": "Invite (resend)", reset: "Password reset" };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function UserManagement() {
  const [tab, setTab] = useState("members");
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  const loadLogs = () => api.get("/email-logs").then(({ data }) => setLogs(data)).catch(() => {});
  useEffect(() => { load(); loadLogs(); }, []);

  const invite = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/users/invite", form);
      setInviteLink(data.invite_url);
      setEmailSent(!!data.email_sent);
      toast.success(data.email_sent ? "Invite emailed" : "Invite created");
      load();
      loadLogs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resend = async (id) => {
    setResendingId(id);
    try {
      const { data } = await api.post(`/users/${id}/resend-invite`);
      setInviteLink(data.invite_url);
      setEmailSent(!!data.email_sent);
      setForm({ email: "", name: "", role: "user" });
      setOpen(true);
      toast.success(data.email_sent ? "Invite re-emailed" : "New invite link generated");
      loadLogs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResendingId(null);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => { setForm({ email: "", name: "", role: "user" }); setInviteLink(""); setEmailSent(false); };

  return (
    <div data-testid="user-management-page">
      <TopNav />
      <div className="h-16 flex items-center justify-between px-8 bg-white border-b border-slate-200 sticky top-0 z-10">
        <div>
          <h1 className="font-heading font-black text-xl tracking-tighter">User Management</h1>
          <p className="text-xs text-slate-500">{users.length} members · invite-only access</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <button data-testid="invite-user-button"
              className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 transition-all">
              <Plus className="h-4 w-4" /> Invite User
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tighter">Invite a new user</DialogTitle>
              <DialogDescription>Generate a secure invite link to share with a new team member.</DialogDescription>
            </DialogHeader>
            {inviteLink ? (
              <div data-testid="invite-link-section" className="py-2">
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 mb-4">
                  {emailSent
                    ? `Invite emailed to ${form.email || "the user"}. You can also copy the link below.`
                    : "Invite created. Email delivery is off — copy this link and send it via Outlook."}
                </div>
                <Lbl>Invite link</Lbl>
                <div className="flex items-center gap-2 mt-1.5">
                  <input data-testid="invite-link-input" readOnly value={inviteLink}
                    className="flex-1 border border-slate-200 px-3 py-2 text-xs font-mono bg-slate-50 rounded-none" />
                  <button data-testid="copy-invite-link" onClick={copy}
                    className="border border-slate-200 p-2 hover:bg-slate-900 hover:text-white transition-all">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <DialogFooter className="mt-6">
                  <button onClick={() => { setOpen(false); reset(); }}
                    className="bg-slate-900 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-slate-800">Done</button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-2">
                  <label className="block space-y-1.5"><Lbl>Full name</Lbl>
                    <Inp data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                  </label>
                  <label className="block space-y-1.5"><Lbl>Email</Lbl>
                    <Inp data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" />
                  </label>
                  <label className="block space-y-1.5"><Lbl>Role</Lbl>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="invite-role" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                </div>
                <DialogFooter>
                  <button data-testid="send-invite-button" onClick={invite} disabled={saving || !form.email || !form.name}
                    className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">
                    {saving ? "Creating…" : "Generate Invite"}
                  </button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="px-8 pt-6">
        <div className="flex gap-1 border-b border-slate-200">
          <button data-testid="tab-members" onClick={() => setTab("members")}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === "members" ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            Team Members
          </button>
          <button data-testid="tab-directory" onClick={() => setTab("directory")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === "directory" ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Users className="h-4 w-4" /> Team Directory
          </button>
          <button data-testid="tab-email-log" onClick={() => { setTab("log"); loadLogs(); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === "log" ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Mail className="h-4 w-4" /> Email Activity
          </button>
        </div>
      </div>

      <div className="px-8 pb-8 pt-6">
        {tab === "directory" ? (
          <DirectoryTab onInvited={() => { load(); loadLogs(); }} />
        ) : tab === "members" ? (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-table">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-xs uppercase">
                        {(u.name || u.email).charAt(0)}
                      </div>
                      {u.name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><span className="text-xs border border-slate-200 px-2 py-0.5 uppercase">{u.role}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {u.status !== "active" && (
                        <button data-testid={`resend-invite-${u.id}`} onClick={() => resend(u.id)} disabled={resendingId === u.id} title="Resend invite" className="text-slate-400 hover:text-blue-700 disabled:opacity-50">
                          {resendingId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} title="Remove" className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Recipient</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Triggered by</th>
                <th className="px-4 py-3 font-medium text-right">When</th>
              </tr>
            </thead>
            <tbody data-testid="email-log-table">
              {logs.map((l, i) => {
                const s = EMAIL_STATUS[l.status] || EMAIL_STATUS.skipped;
                const Icon = s.icon;
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`email-log-row-${i}`}>
                    <td className="px-4 py-3 font-medium">{TYPE_LABELS[l.type] || l.type}</td>
                    <td className="px-4 py-3 text-slate-600">{l.to}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.cls}`} title={l.error || ""}>
                        <Icon className="h-4 w-4" /> {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{l.triggered_by || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 text-right">{fmtTime(l.created_at)}</td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500" data-testid="email-log-empty">No email activity yet. Invites and password resets will appear here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}

const STORE_ORDER = ["18177", "18179", "18396", "18470", "19380"];

function DirectoryTab({ onInvited }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // profile being invited
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/profiles").then(({ data }) => setProfiles(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openInvite = (p) => { setTarget(p); setEmail(p.invited_email || ""); setResult(null); };
  const close = () => { setTarget(null); setEmail(""); setResult(null); };

  const sendInvite = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/profiles/${target.id}/invite`, { email });
      setResult(data);
      toast.success(data.email_sent ? "Invite emailed" : "Invite link created");
      load();
      onInvited && onInvited();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!result?.invite_url) return;
    navigator.clipboard.writeText(result.invite_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div data-testid="directory-loading" className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading directory…</div>;
  }
  if (profiles.length === 0) {
    return <div data-testid="directory-empty" className="text-center text-slate-500 py-12">No imported team members yet.</div>;
  }

  const groups = {};
  profiles.forEach((p) => {
    const key = STORE_ORDER.includes(p.store_code) ? p.store_code : "other";
    (groups[key] = groups[key] || []).push(p);
  });
  const orderedKeys = [...STORE_ORDER.filter((k) => groups[k]), ...(groups.other ? ["other"] : [])];

  return (
    <div data-testid="directory-tab" className="space-y-8">
      <p className="text-sm text-slate-500 -mt-2">
        These are team members imported from your previous system. They don't have logins yet — send an invite to let them set a password and sign in.
      </p>
      {orderedKeys.map((key) => (
        <div key={key} data-testid={`directory-group-${key}`}>
          <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-700 mb-3">
            {key === "other" ? "No store assigned" : `Store ${key}`}
            <span className="ml-2 text-slate-400 font-normal normal-case tracking-normal">({groups[key].length})</span>
          </h3>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {groups[key].map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`directory-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-xs uppercase">
                          {(p.display_name || "?").charAt(0)}
                        </div>
                        {p.display_name || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.legacy_roles?.length ? p.legacy_roles : [p.role]).map((r, i) => (
                          <span key={i} className={`text-xs border px-2 py-0.5 ${r === "admin" ? "bg-blue-50 text-blue-700 border-blue-200" : "border-slate-200 text-slate-600"}`}>{fmtRole(r)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {p.is_login_account
                        ? <span data-testid={`directory-status-invited-${p.id}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Invited{p.invited_email ? ` · ${p.invited_email}` : ""}</span>
                        : <span className="text-xs text-slate-400">No login</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`invite-profile-${p.id}`} onClick={() => openInvite(p)}
                        className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-blue-700 hover:text-white hover:border-blue-700 transition-all">
                        <UserPlus className="h-3.5 w-3.5" /> {p.is_login_account ? "Re-invite" : "Send invite"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="rounded-none sm:max-w-lg" data-testid="invite-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tighter">Invite {target?.display_name}</DialogTitle>
            <DialogDescription>Send a secure invite so this team member can set a password and sign in. We'll keep their store and role.</DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="py-2" data-testid="invite-profile-result">
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 mb-4">
                {result.email_sent ? `Invite emailed to ${email}. You can also copy the link below.` : "Invite created. Copy this link and send it manually."}
              </div>
              <Lbl>Invite link</Lbl>
              <div className="flex items-center gap-2 mt-1.5">
                <input data-testid="invite-profile-link" readOnly value={result.invite_url}
                  className="flex-1 border border-slate-200 px-3 py-2 text-xs font-mono bg-slate-50 rounded-none" />
                <button onClick={copyLink} className="border border-slate-200 p-2 hover:bg-slate-900 hover:text-white transition-all">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <DialogFooter className="mt-6">
                <button onClick={close} className="bg-slate-900 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-slate-800">Done</button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <label className="block space-y-1.5"><Lbl>Email address</Lbl>
                  <Inp data-testid="invite-profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
                </label>
                <div className="text-xs text-slate-500">
                  Store: <span className="font-medium text-slate-700">{target?.store_code || "—"}</span> · Role:{" "}
                  <span className="font-medium text-slate-700">{fmtRole(target?.role)}</span>
                </div>
              </div>
              <DialogFooter>
                <button data-testid="invite-profile-submit" onClick={sendInvite} disabled={saving || !email}
                  className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">
                  {saving ? "Sending…" : "Send invite"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
